import { 
  Document, 
  Packer, 
  Paragraph, 
  Table,
  PageOrientation,
  SectionType,
} from 'docx'

import { ComprehensiveConverterOptions, ProcessingContext, ProcessingState } from './types'
import { processCodeBlock } from './processors/codeBlock'
import { processTable } from './processors/table'
import { processHeading } from './processors/heading'
import { processList } from './processors/list'
import { processBlockquote } from './processors/blockquote'
import { processHorizontalRule } from './processors/horizontalRule'
import { processParagraph } from './processors/paragraph'

/**
 * Comprehensive markdown to DOCX converter supporting all standard markdown syntax
 */
export function convertMarkdownComprehensively(
  markdown: string, 
  options: ComprehensiveConverterOptions = {}
): Document {
  const {
    font = 'Calibri',
    fontSize = 22,
    headerFontSize = 24,
    nameFontSize = 32,
    title = 'Professional Resume',
    creator = 'Jobscan Comprehensive Markdown to DOCX Converter',
    margins = {
      top: '1in',
      right: '1in', 
      bottom: '1in',
      left: '1in'
    }
  } = options

  const context: ProcessingContext = {
    font,
    fontSize,
    headerFontSize,
    nameFontSize
  }

  const state: ProcessingState = {
    isInCodeBlock: false,
    codeBlockContent: [],
    isInTable: false
  }

  const lines = markdown.split('\n')
  const children: (Paragraph | Table)[] = []
  
  let i = 0

  while (i < lines.length) {
    const line = lines[i]
    const trimmedLine = line.trim()

    // Skip empty lines
    if (!trimmedLine) {
      i++
      continue
    }

    // Try processing as different markdown elements
    const codeBlockResult = processCodeBlock(line, trimmedLine, state, context)
    if (codeBlockResult !== null) {
      children.push(codeBlockResult)
      i++
      continue
    }

    // Skip if we're inside a code block
    if (state.isInCodeBlock) {
      i++
      continue
    }

    const tableResult = processTable(lines, i, state, context)
    if (tableResult.paragraph !== null) {
      children.push(tableResult.paragraph)
      i = tableResult.newIndex
      continue
    }

    const headingResult = processHeading(trimmedLine, context)
    if (headingResult !== null) {
      children.push(headingResult)
      i++
      continue
    }

    const listResult = processList(trimmedLine, context)
    if (listResult !== null) {
      children.push(listResult)
      i++
      continue
    }

    const blockquoteResult = processBlockquote(trimmedLine, context)
    if (blockquoteResult !== null) {
      children.push(blockquoteResult)
      i++
      continue
    }

    const hrResult = processHorizontalRule(trimmedLine, context)
    if (hrResult !== null) {
      children.push(hrResult)
      i++
      continue
    }

    const paragraphResult = processParagraph(trimmedLine, context)
    if (paragraphResult !== null) {
      children.push(paragraphResult)
    }

    i++
  }

  return new Document({
    sections: [{
      properties: {
        page: {
          size: {
            width: '8.5in',
            height: '11in',
          },
          margin: {
            top: margins.top || '1in',
            right: margins.right || '1in',
            bottom: margins.bottom || '1in', 
            left: margins.left || '1in',
          },
          orientation: PageOrientation.PORTRAIT,
        },
        type: SectionType.CONTINUOUS,
      },
      children
    }],
    title,
    creator,
    styles: {
      default: {
        document: {
          run: {
            font,
            size: fontSize,
          },
          paragraph: {
            spacing: {
              line: 276, // 1.15 line spacing
            },
          },
        },
      },
    },
  })
}

/**
 * Convert markdown comprehensively and return blob
 */
export async function convertMarkdownComprehensivelyToBlob(
  markdown: string,
  options: ComprehensiveConverterOptions = {}
): Promise<Blob> {
  const document = convertMarkdownComprehensively(markdown, options)
  return await Packer.toBlob(document)
}

/**
 * Convert markdown comprehensively and trigger download
 */
export async function downloadMarkdownComprehensively(
  markdown: string,
  options: ComprehensiveConverterOptions = {}
): Promise<void> {
  const { filename = 'resume.docx' } = options
  
  try {
    const blob = await convertMarkdownComprehensivelyToBlob(markdown, options)
    
    // Create download link
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error converting markdown comprehensively to DOCX:', error)
    throw error
  }
}

// Re-export types for convenience
export type { ComprehensiveConverterOptions } from './types'